// Section 19 and 13: Wild animals. 6% per season x Threat level, Spring and Autumn bias; a manned
// palisade makes them 0.3x as likely (Section 9). A working forager or hunter is injured; in Harsh
// land a child under 6 left unwatched is taken 20% of the time. Choices: Hunt it (2 hunters, each
// 15% injured, +4 food) or Avoid it (forage 0.7x for 2 seasons).
// Constants: config.events['wild-animals'], config.buildings.palisade.wildAnimals.
import type { Config } from '../../config';
import {
  choiceOutcome,
  choiceRng,
  effectDelta,
  effectParamModifiers,
  eventEntry,
  eventPrompt,
  eventRng,
} from '../../core/content-helpers';
import { isWorking, palisadeStrength, skillOf, uncoveredUnderSix } from '../../core/queries';
import { defineEvent, type EventChoiceDef } from '../../core/registry';
import type { Delta, Person, PersonId, ReadContext } from '../../core/types';

function sorted(ctx: ReadContext): Person[] {
  return (Object.keys(ctx.state.people).sort() as PersonId[])
    .map((id) => ctx.state.people[id])
    .filter((p): p is Person => p !== undefined);
}

interface Attack {
  injured: Person | null;
  child: Person | null;
}

function attack(ctx: ReadContext, childTaken: number): Attack {
  const rng = eventRng(ctx, 'wild-animals', 'attack');
  const exposed = sorted(ctx).filter(
    (p) => (p.job === 'forager' || p.job === 'hunter') && isWorking(p),
  );
  const injured = exposed.length > 0 ? rng.pick(exposed) : null;
  let child: Person | null = null;
  if (ctx.state.options.land === 'harsh') {
    const unwatched = uncoveredUnderSix(ctx.cfg, ctx.state);
    if (unwatched.length > 0 && rng.float() < childTaken)
      child = ctx.state.people[rng.pick(unwatched)] ?? null;
  }
  return { injured, child };
}

/** Hunters for the hunt: working hunters first, then foragers, then guards, best hunters first. */
function huntParty(ctx: ReadContext, size: number): Person[] {
  const rank = (p: Person): number => (p.job === 'hunter' ? 0 : p.job === 'forager' ? 1 : 2);
  return sorted(ctx)
    .filter((p) => ['hunter', 'forager', 'guard'].includes(p.job) && isWorking(p))
    .sort(
      (a, b) =>
        rank(a) - rank(b) || skillOf(b, 'hunter') - skillOf(a, 'hunter') || (a.id < b.id ? -1 : 1),
    )
    .slice(0, size);
}

export function wildAnimals(config: Config) {
  const c = config.events['wild-animals'];
  const pct = (x: number): number => Math.round(x * 100);
  const choices: EventChoiceDef[] = [
    {
      id: 'hunt-it',
      label: 'Hunt it',
      preview: (ctx) => [
        {
          key: 'hunt',
          params: {
            hunters: huntParty(ctx, c.huntHunters).length,
            needed: c.huntHunters,
            food: c.huntFood,
            risk: pct(c.huntInjury),
          },
        },
      ],
      apply: (ctx, prompt) => {
        const party = huntParty(ctx, c.huntHunters);
        if (party.length === 0) return [choiceOutcome(prompt, 'hunt-it', { food: 0, hurt: '' })];
        const rng = choiceRng(prompt, 'hunt');
        const food = (c.huntFood * party.length) / c.huntHunters;
        const out: Delta[] = [
          { kind: 'stock', resource: 'food', amount: food, flow: 'production' },
        ];
        const hurt: string[] = [];
        for (const p of party) {
          if (rng.float() >= c.huntInjury) continue;
          hurt.push(p.name);
          out.push({ kind: 'injure', personId: p.id, source: 'event:wild-animals' });
        }
        out.push(choiceOutcome(prompt, 'hunt-it', { food, hurt: hurt.join(', ') }));
        return out;
      },
    },
    {
      id: 'avoid',
      label: 'Avoid it',
      preview: () => [{ key: 'avoid', params: { mul: c.avoidForageMul, seasons: c.avoidSeasons } }],
      apply: (ctx, prompt) => [
        effectDelta('wild-animals', 'avoid', ctx.season.turn, c.avoidSeasons, {
          forageMul: c.avoidForageMul,
        }),
        choiceOutcome(prompt, 'avoid', { seasons: c.avoidSeasons }),
      ],
    },
  ];
  return defineEvent({
    id: 'wild-animals',
    label: 'Wild animals',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['spring', 'autumn'],
    foreshadow: null,
    ledgerTag: null,
    weight: (ctx) =>
      c.baseChance *
      (1 - (1 - ctx.cfg.buildings.palisade.wildAnimals) * palisadeStrength(ctx.cfg, ctx.state)),
    text: (ctx) => {
      const a = attack(ctx, c.childTaken);
      const variant =
        a.injured !== null && a.child !== null
          ? 'hurt-child'
          : a.injured !== null
            ? 'hurt'
            : a.child !== null
              ? 'child'
              : 'none';
      return eventEntry('wild-animals', {
        variant,
        name: a.injured?.name ?? '',
        child: a.child?.name ?? '',
      });
    },
    apply: (ctx) => {
      const a = attack(ctx, c.childTaken);
      const out: Delta[] = [];
      if (a.injured !== null)
        out.push({ kind: 'injure', personId: a.injured.id, source: 'event:wild-animals' });
      if (a.child !== null)
        out.push({
          kind: 'kill',
          personId: a.child.id,
          cause: 'wild-animal',
          source: 'event:wild-animals',
        });
      out.push(eventPrompt(ctx, 'wild-animals', choices, { defaultChoiceId: 'avoid' }));
      return out;
    },
    choices,
    modifierTargets: ['output'],
    modifiers: effectParamModifiers,
  });
}
